import { Theme } from '@mui/material/styles'

import { surfaceHover } from '../hover'
import { layout } from '../tokens.generated'

// ----------------------------------------------------------------------
// A menu is a popover with a list in it, so its surface (radius, edge, shadow)
// is decided once on `MuiPopover` and applies here through the shared paper.
// What is decided here is the inside: an 8px inset so the rounded items do not
// touch the edge, and a 32px dense item that grows to the touch target on a
// coarse pointer.
//
// The inset sits on the list, not the paper. `MenuList` already pads itself
// 8px top and bottom, so padding the paper as well would stack to 16 on the
// block axis. `MenuPopover`, the app's kebab menu, is a bare `Popover` that
// pads its own paper and is not touched by this.
// ----------------------------------------------------------------------

export default function Menu(theme: Theme) {
  return {
    MuiMenu: {
      styleOverrides: {
        list: {
          padding: theme.spacing(1),
        },
      },
    },
    // The menu item radius used to be re-declared in every kebab menu in the
    // app, at a value on no ramp. It is decided here once: the system decides
    // the look, the screen never does.
    MuiMenuItem: {
      styleOverrides: {
        root: {
          'minHeight': layout.controlHeightSmall,
          'borderRadius': theme.radii.control,
          // The one surface answer, the same value a row and a nav item take.
          // This was `action.hover`, an 8% grey wash, so a kebab menu and the
          // row that opened it lit up two different colours.
          ...surfaceHover(theme),
          '&.Mui-selected': {
            'color': theme.palette.primary.dark,
            'backgroundColor': theme.palette.primary.lighter,
            // The fill already says which one is selected, so the ink is what
            // answers. Repainting the same fill read as nothing happening.
            '&:hover': {
              backgroundColor: theme.palette.primary.lighter,
              color: theme.hover.selectedInk,
            },
          },
          // Every interactive control is at least the touch target on a
          // touch viewport. The dense height is for a pointer that can aim.
          '@media (pointer: coarse)': {
            minHeight: layout.controlHeightLarge,
          },
        },
      },
    },
  }
}
